<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <h4 qid="maintenance-create-p-title">
      {{$t('maintenance.policies.create.title')}}
    </h4>

    <div class="card">
      <div class="card-body">
        <div class="form-centered">
          <b-alert v-if="error.length"
                   show
                   variant="danger"
                   qid="create-p-error"
          >
            {{ error }}
          </b-alert>
          <b-form @submit="formSubmit" qid="create-p-form">
            <div class="form-container">
              <b-form-group>
                <label qid="create-p-name-label">
                  {{ $t('maintenance.policies.create.name_label') }}
                </label>
                <b-form-input
                  qid="create-p-form-name"
                  type="text"
                  v-model="pForm.name"
                  required
                  :maxLength="255"
                  :placeholder="$t('maintenance.policies.create.name_placeholder')"
                ></b-form-input>
              </b-form-group>
              <b-form-group>
                <label qid="create-p-description-label">
                  {{ $t('maintenance.policies.create.description_label') }}
                </label>
                <b-form-textarea
                  qid="create-p-form-description"
                  v-model="pForm.description"
                  rows="6"
                  max-rows="6"
                  :placeholder="$t('maintenance.policies.create.description_placeholder')"
                ></b-form-textarea>
              </b-form-group>
              <b-form-group>
                <label qid="create-p-implementation-status-label">
                  {{ $t('maintenance.policies.create.category_label') }}
                </label>
                <multiselect
                    v-model="pForm.category"
                    :options="categoryItems"
                    :placeholder="$t('maintenance.policies.create.category_placeholder')"
                    label="name"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    qid="create-p-form-implementation-status"
                ></multiselect>
              </b-form-group>
              <b-form-group>
                <label qid="create-s-tags-label">
                  {{ $t('systems.create.tags_label') }}
                </label>
                <multiselect
                    v-model="selectedTags"
                    :options="tags"
                    :multiple="true"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    :taggable="true"
                    track-by="name_translated"
                    :placeholder="$t('systems.create.tags_placeholder')"
                    label="name_translated"
                    qid="create-do-tags-select"
                    @search-change="loadTags"
                    @tag="addTag"
                ></multiselect>
              </b-form-group>
              <b-form-group>
                <label qid="create-s-users-label">
                  {{ $t('systems.create.owners_label') }}
                </label>
                <multiselect
                    v-model="selectedUsers"
                    :options="users"
                    :multiple="true"
                    :taggable="false"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    track-by="slug"
                    :placeholder="$t('systems.create.owners_placeholder')"
                    label="name"
                    @remove="removeUserFromDropdown"
                    @search-change="loadUsers"
                    qid="create-s-users-select"
                >
                  <template slot="tag" slot-scope="props">
                    <span></span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">
                      {{ props.option.name }}
                    </span>
                  </template>
                </multiselect>
                <div class="form-control auto-height mt-1" v-if="selectedUsers && selectedUsers.length">
                  <div v-for="(user, index) in selectedUsers" class="mt-1 mb-1">
                    <div class="row">
                      <div class="col-sm-1">
                        <font-awesome-icon
                          icon="star"
                          :class="{'color-yellow': user.owner, 'text-light': !user.owner}"
                          class="cursor-pointer"
                          @click="setOwner(index)"
                        />
                      </div>
                      <div class="col-sm-10">
                        <Avatar :object="user" size="xs" :noOwner="true"></Avatar>
                        <span class="ml-2">{{ user.name }}</span>
                      </div>
                      <div class="col-sm-1">
                        <font-awesome-icon
                          icon="times"
                          class="cursor-pointer"
                          @click="removeUser(index)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <span class="text-light">
                  * {{ $t('maintenance.policies.create.owners_tip') }}
                </span>
              </b-form-group>

              <div class="mb-3">
                <label qid="edit-user-phone-label">
                  {{ $t('administration.users.create.organization_label') }}
                </label>
                <OrganizationTreeView
                    @updatedCompany="updateSelectedCompany"
                ></OrganizationTreeView>
              </div>
            </div>
            <div class="text-right">
              <b-button class="mt-3"
                        variant="secondary"
                        @click="goToList"
                        qid="create-p-cancel-button"
              >
                {{ $t('maintenance.policies.create.cancel') }}
              </b-button>
              <b-button type="submit"
                        class="mt-3 ml-3"
                        variant="success"
                        :disabled="buttonDisabled"
                        qid="create-p-submit-button"
              >
                <span v-if="buttonDisabled" class="mr-1">
                  <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                </span>
                {{ $t('maintenance.policies.create.submit') }}
              </b-button>
            </div>
          </b-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import OrganizationTreeView from '@/components/organizations/OrganizationTreeView.vue';

  export default {
    name: 'CreatePolicy',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.policies.title'),
            to: '/policies'
          },
          {
            text: this.$t('maintenance.policies.create.title'),
            active: true
          }
        ],
        pForm: {
          name: '',
          description: '',
          category: [],
          users: [],
          tags: [],
          organization_id: '',
          client:''
        },
        tags: [],
        newTags: [],
        selectedTags: [],
        error: '',
        users: [],
        selectedUsers: [],
        inputLink: '',
        inputLinkName: '',
        selectedFiles: [],
        categoryInput: '',
        categoryItems: [
          { name: this.$t('maintenance.policies.category.policy'), value: 'policy' },
          { name: this.$t('maintenance.policies.category.procedure'), value: 'procedure' },
          { name: this.$t('maintenance.policies.category.supporting_document'), value: 'supporting_document' }
        ],
        buttonDisabled: false
      }
    },
    components: {
      Avatar,
      OrganizationTreeView
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getFiles() {
        return this.selectedFiles
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadUsers();
        this.loadTags();
      }
    },
    methods: {
      goToList() {
        this.$router.push('/policies')
      },
      resetFile() {
        let imagefile = document.querySelector('#file-p');
        imagefile.value = '';
      },
      onFileChange() {
        let files = document.querySelector('#file-p');
        // in order for v-for to re render, we need to set array to empty
        this.selectedFiles = [];
        // And then push item by item into that array
        for (var i = 0; i < files.files.length; i++) {
          this.selectedFiles.push(files.files[i])
        }
      },
      handleLink() {
        // If there's a link in input, add it to links array
        if (this.inputLink !== '') {
          if (!this.inputLink.startsWith('http')) {
            this.inputLink = 'http://' + this.inputLink
          }
          this.pForm.links.push({url: this.inputLink, name: this.inputLinkName})
          // Reset link input form
          this.inputLink = ''
          this.inputLinkName = ''
        }
      },
      removeLinkItem(index) {
        // Remove link item from the links array
        this.pForm.links.splice(index, 1);
      },
      updateSelectedCompany(item) {
        this.pForm.organization_id = item.id
      },
      formSubmit(evt) {
        let formData = new FormData();

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        let owners = []
        let selectedOwner = false;
        // Append list of users to FormData
        if (this.selectedUsers && this.selectedUsers.length) {
          for (var i = 0; i < this.selectedUsers.length; i++) {
            formData.append("users[]", this.selectedUsers[i].id);
            if (this.selectedUsers[i].owner) {
              selectedOwner = true;
              owners.push(this.selectedUsers[i].id)
            }
          }
        } else {
          selectedOwner = true;
        }

        if (owners.length) {
          for (var i = 0; i < owners.length; i++) {
            formData.append("owner[]", owners[i]);
          }
        }

        if (this.selectedTags && this.selectedTags.length) {
          for (var i = 0; i < this.selectedTags.length; i++) {
            formData.append("tags[]", this.selectedTags[i].name_translated);
          }
        }

        if (this.pForm.category && this.pForm.category.value) {
          formData.append("category", this.pForm.category.value);
        }
        formData.append("organization_id", this.pForm.organization_id);
        formData.append("name", this.pForm.name);
        formData.append("description", this.pForm.description);
        formData.append("client", this.getClient.slug);

        this.buttonDisabled = true;
        // Post FormData object to API
        piincHttp.post('/policies', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          self.$toastr('success', self.$t('maintenance.policies.create.success'))
          self.buttonDisabled = false;
          self.goToList();
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
            if (error.data.errors['files.0']) {
              self.error += ' ' + error.data.errors['files.0'][0]
            }
          }
        })

      },
      addTag(newTag) {
        const tag = {
          name: newTag,
          translatable: newTag,
          name_translated: newTag
        }
        this.selectedTags.push(tag)
        this.newTags.push(tag)
      },
      removeUser(index) {
        let selectedItem = this.selectedUsers[index]
        selectedItem.owner = false;
        this.$set(this.selectedUsers, index, selectedItem)
        // Remove item from selected users array
        this.selectedUsers.splice(index, 1);
      },
      removeUserFromDropdown(item) {
        item.owner = false;
      },
      setOwner(index) {
        let selectedItem = this.selectedUsers[index]
        selectedItem.owner = !selectedItem.owner;
        this.$set(this.selectedUsers, index, selectedItem)
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50
          }
        }).then(function(response) {
          self.users = response.data.items
          if (self.users && !self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params:
          {
            client: this.getClient.slug,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.tags = response.data
          if (self.tags && !self.tags.length && !query) {
            self.tags.push({name_translated: self.$t('system.no_results'), $isDisabled: true})
          }
          for (var i = 0; i < self.newTags.length; i++) {
            self.tags.push(self.newTags[i]);
          }
        }, function() {});
      }
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        this.loadUsers();
        this.loadTags();
      }
    }
  }
</script>
